import { useRef, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { CategoriaDeMeta, DiferenciaDeCoste, ExtractoDeYCloud, RecargaDelMonedero, ResolucionDeDiferencia } from '../../api/ops/backoffice';
import { Chip } from '../../componentes/Chip';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { errorDeCampo } from '../../logica/acceso';
import { aCentimos, dinero } from '../../logica/dinero';
import { claveMes, type Mes } from '../../partners/logica';
import { ERROR_MOTIVO, motivoValido, textoDeError } from '../negocios/logica';
import {
    CATEGORIAS,
    CATEGORIAS_COSTE,
    ERROR_COSTE,
    ERROR_MENSAJES,
    AVISO_MONEDERO,
    ERROR_IMPORTE_RECARGA,
    ERROR_REFERENCIA_RECARGA,
    MONEDA_MONEDERO,
    NOTA_COSTE,
    PAISES,
    RESOLUCIONES,
    aDiezmilesimas,
    iniciosDeTrimestre,
    leerCsvYCloud,
    mensajesDeAjuste,
    mesFuturo,
    textoDelMes,
    filaRecarga,
    textoResolver,
    type CsvLeido,
    type FilaConciliacion,
} from './logica';

/**
 * **Resolver una diferencia** (prototipo `vy`, `resolveWhatsAppCostDiscrepancy`): corregir el registro, ajustar el paquete
 * del negocio (con los mensajes que se ajustan, con signo), reclamar a YCloud o asumir el coste. Siempre con motivo.
 */
export function VentanaResolver({ ops, movil, fila, discrepancia, resolucion, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; fila: FilaConciliacion; discrepancia: DiferenciaDeCoste; resolucion: ResolucionDeDiferencia; onCerrar: () => void; onHecho: (d: DiferenciaDeCoste) => void }) {
    const avisar = useAvisoBreve();
    const r = RESOLUCIONES[resolucion];
    const ajustar = resolucion === 'balance_adjusted';
    const [mensajes, setMensajes] = useState('');
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errores, setErrores] = useState<{ mensajes?: string; motivo?: string }>({});
    const errorMotivo = errores.motivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);
    const errorMensajes = errores.mensajes ?? (intentado && ajustar && mensajesDeAjuste(mensajes) === null ? ERROR_MENSAJES : null);
    const resolver = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        const n = mensajesDeAjuste(mensajes);
        if (!motivoValido(motivo) || (ajustar && n === null)) {
            return;
        }
        setOcupado(true);
        ops.resolveWhatsAppCostDiscrepancy(discrepancia.id, { resolution: resolucion, ...(ajustar && n !== null ? { adjustment_messages: n } : {}), reason: motivo.trim() })
            .then(onHecho)
            .catch((e: unknown) => {
                setOcupado(false);
                const de = { mensajes: errorDeCampo(e, 'adjustment_messages'), motivo: errorDeCampo(e, 'reason') };
                if (de.mensajes !== null || de.motivo !== null) {
                    setErrores({ mensajes: de.mensajes ?? undefined, motivo: de.motivo ?? undefined });
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <Ventana abierta titulo={r.titulo} texto={textoResolver(fila, resolucion)} accion={r.boton} accionIcono={r.icono} ocupado={ocupado} movil={movil} onAccion={resolver} onCerrar={onCerrar}>
            {ajustar && (
                <Campo
                    etiqueta="Mensajes que se ajustan en su paquete"
                    tipo="number"
                    modo="numeric"
                    valor={mensajes}
                    onChange={(v) => {
                        setMensajes(v);
                        setErrores((x) => ({ ...x, mensajes: undefined }));
                    }}
                    error={errorMensajes}
                />
            )}
            <Campo
                etiqueta="Motivo"
                valor={motivo}
                onChange={(v) => {
                    setMotivo(v);
                    setErrores((x) => ({ ...x, motivo: undefined }));
                }}
                max={500}
                error={errorMotivo}
                ayuda="Obligatorio. Queda en Auditoría."
                onEnter={resolver}
            />
        </Ventana>
    );
}

/**
 * **Coste de WhatsApp** (RV Panel `formulario`, prototipo `pIsCoste`, `createWhatsAppCostRate`): categoría de Meta, país,
 * coste por mensaje sin IVA, desde qué trimestre vale y motivo. No se edita: el nuevo cierra el anterior.
 */
export function PanelCoste({ abierto, ops, movil, ahora, onCerrar, onHecho }: { abierto: boolean; ops: Operaciones; movil: boolean; ahora: Date; onCerrar: () => void; onHecho: () => void }) {
    return abierto ? <FormCoste ops={ops} movil={movil} ahora={ahora} onCerrar={onCerrar} onHecho={onHecho} /> : null;
}

function FormCoste({ ops, movil, ahora, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; ahora: Date; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [fechas] = useState(() => iniciosDeTrimestre(ahora));
    const [categoria, setCategoria] = useState<CategoriaDeMeta>('marketing');
    const [pais, setPais] = useState('ES');
    const [coste, setCoste] = useState('');
    const [desde, setDesde] = useState(fechas[0]?.valor ?? '');
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errores, setErrores] = useState<{ coste?: string; motivo?: string }>({});
    // En diezmilésimas: el coste por mensaje lleva 4 decimales (G1149).
    const diezmilesimas = aDiezmilesimas(coste);
    const errorCoste = errores.coste ?? (intentado && diezmilesimas === null ? ERROR_COSTE : null);
    const errorMotivo = errores.motivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);
    const registrar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (diezmilesimas === null || !motivoValido(motivo)) {
            return;
        }
        setOcupado(true);
        ops.createWhatsAppCostRate({ pricing_category: categoria, country: pais, unit_cost: { amount: diezmilesimas, currency: 'EUR' }, valid_from: desde, reason: motivo.trim() })
            .then(() => {
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const de = { coste: errorDeCampo(e, 'unit_cost.amount') ?? errorDeCampo(e, 'unit_cost'), motivo: errorDeCampo(e, 'reason') };
                if (de.coste !== null || de.motivo !== null) {
                    setErrores({ coste: de.coste ?? undefined, motivo: de.motivo ?? undefined });
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Coste de WhatsApp"
            eyebrow="Conciliación YCloud"
            subtitulo="Las tarifas nuevas de Meta, cada trimestre."
            icono="scale"
            accion="Registrar el coste"
            accionIcono="check"
            ocupado={ocupado}
            onAccion={registrar}
        >
            <div className="rv-boy-form">
                <Desplegable
                    etiqueta="Categoría de Meta"
                    valor={categoria}
                    opciones={CATEGORIAS_COSTE}
                    onChange={(v) => {
                        setCategoria(CATEGORIAS_COSTE.find((c) => c.valor === v)?.valor ?? 'marketing');
                    }}
                />
                <Desplegable etiqueta="País" valor={pais} opciones={PAISES} onChange={setPais} />
                <Campo
                    etiqueta="Coste por mensaje, sin IVA"
                    tipo="number"
                    valor={coste}
                    onChange={(v) => {
                        setCoste(v);
                        setErrores((x) => ({ ...x, coste: undefined }));
                    }}
                    prefijo="€ "
                    error={errorCoste}
                    ayuda="Con hasta 4 decimales, por ejemplo 0,0585."
                />
                <Desplegable etiqueta="Vale desde" valor={desde} opciones={fechas} onChange={setDesde} />
                <Campo
                    etiqueta="Motivo"
                    valor={motivo}
                    onChange={(v) => {
                        setMotivo(v);
                        setErrores((x) => ({ ...x, motivo: undefined }));
                    }}
                    max={500}
                    error={errorMotivo}
                    ayuda="Obligatorio. Queda en Auditoría."
                />
            </div>
            <div className="rv-boy-nota">
                <Icono nombre="info" />
                {NOTA_COSTE}
            </div>
        </PanelLateral>
    );
}

/** Los meses que se pueden elegir para el extracto: el elegido en la pantalla y los 11 anteriores (nunca uno futuro). */
function mesesDelExtracto(mes: Mes, ahora: Date): Mes[] {
    const lista: Mes[] = [];
    for (let i = 0; i < 12; i++) {
        const total = mes.y * 12 + mes.m - i;
        const m = { y: Math.floor(total / 12), m: total % 12 };
        if (!mesFuturo(m, ahora)) {
            lista.push(m);
        }
    }
    return lista;
}

/**
 * **Importar el extracto de YCloud** (G1156, `importWhatsAppCostStatement`): se elige el CSV que se descarga de YCloud y el
 * número y el mes del extracto; el panel convierte el CSV en líneas, enseña la vista previa y los errores del archivo, y al
 * importar **se concilia** (sin un paso «Conciliar» aparte). El total sin IVA ya no se teclea: lo suma el back de las líneas
 * (G1172, ADR-0268).
 */
// C.68: el panel del dibujo 1.22.
export function PanelImportar(p: { abierto: boolean; ops: Operaciones; movil: boolean; mes: Mes; ahora: Date; onCerrar: () => void; onHecho: (e: ExtractoDeYCloud, mes: Mes) => void }) {
    return p.abierto ? <FormImportar {...p} /> : null;
}

function FormImportar({ ops, movil, mes, ahora, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; mes: Mes; ahora: Date; onCerrar: () => void; onHecho: (e: ExtractoDeYCloud, mes: Mes) => void }) {
    const avisar = useAvisoBreve();
    const [meses] = useState(() => mesesDelExtracto(mes, ahora));
    const [archivo, setArchivo] = useState<{ nombre: string; kb: number } | null>(null);
    const entrada = useRef<HTMLInputElement>(null);
    const [leido, setLeido] = useState<CsvLeido | null>(null);
    const [numero, setNumero] = useState('');
    const [periodo, setPeriodo] = useState(claveMes(mes));
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [deApi, setDeApi] = useState<Partial<Record<'numero' | 'lineas', string>>>({});
    const errores = {
        archivo: intentado && leido === null ? 'Elige el archivo del extracto.' : null,
        numero: deApi.numero ?? (intentado && numero.trim() === '' ? 'Escribe el número del extracto.' : null),
    };

    const elegir = (f: File | undefined) => {
        setDeApi({});
        if (f === undefined) {
            return;
        }
        setArchivo({ nombre: f.name, kb: Math.max(1, Math.round(f.size / 1024)) });
        f.text()
            .then((texto) => {
                setLeido(leerCsvYCloud(texto));
            })
            .catch(() => {
                setLeido({ lineas: [], errores: ['No se ha podido leer el archivo.'] });
            });
    };

    const importar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (leido === null || leido.lineas.length === 0 || leido.errores.length > 0 || numero.trim() === '') {
            return;
        }
        setOcupado(true);
        const delMes = meses.find((m) => claveMes(m) === periodo) ?? mes;
        // G1172, G1197: el total no se teclea ni se manda (`total_excl_tax` es opcional); lo suma el back de las líneas.
        ops.importWhatsAppCostStatement({ issuer: 'ycloud', number: numero.trim(), period: periodo, lines: leido.lineas })
            .then((e) => {
                onHecho(e, delMes);
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const api = { numero: errorDeCampo(e, 'number'), lineas: errorDeCampo(e, 'lines') };
                if (Object.values(api).some((x) => x !== null)) {
                    setDeApi(Object.fromEntries(Object.entries(api).filter(([, x]) => x !== null)));
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };

    const vista = leido?.lineas.slice(0, 8) ?? [];
    const fallos = [...(leido?.errores ?? []), ...(deApi.lineas === undefined ? [] : [deApi.lineas])];
    const nLineas = (n: number) => (n === 1 ? '1 línea' : `${String(n)} líneas`);
    const cuenta = leido === null ? '' : fallos.length > 0 ? `${nLineas(leido.lineas.length + leido.errores.length)} · ${String(fallos.length)} con errores` : nLineas(leido.lineas.length);
    const pais = (codigo: string) => {
        try {
            return new Intl.DisplayNames('es', { type: 'region' }).of(codigo) ?? codigo;
        } catch {
            return codigo;
        }
    };
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Importar el extracto de YCloud"
            eyebrow="Conciliación YCloud"
            subtitulo="El CSV del mes. Se concilia al importarlo."
            icono="upload"
            // Con errores en el archivo no se importa nada: sin botón (C.68).
            accion={fallos.length > 0 ? '' : 'Importar y conciliar'}
            accionIcono="upload"
            ocupado={ocupado}
            ocupadoTexto="Importando…"
            onAccion={importar}
        >
            <div className="rv-boy-form">
                <input
                    ref={entrada}
                    type="file"
                    accept=".csv,text/csv"
                    aria-label="Archivo CSV de YCloud"
                    className="rv-boy-archivo__input"
                    onChange={(e) => {
                        elegir(e.target.files?.[0]);
                        e.target.value = '';
                    }}
                />
                {archivo === null ? (
                    <button
                        type="button"
                        className={`rv-boy-archivo${errores.archivo === null ? '' : ' rv-boy-archivo--error'}`}
                        onClick={() => {
                            entrada.current?.click();
                        }}
                    >
                        <span className="rv-boy-archivo__baldosa"><Icono nombre="upload" /></span>
                        <strong>Elegir el archivo CSV</strong>
                        <span>El extracto del mes, tal como lo descargas de YCloud.</span>
                    </button>
                ) : (
                    <div className="rv-boy-elegido">
                        <Icono nombre="file-spreadsheet" />
                        <span>
                            <strong>{archivo.nombre}</strong>
                            <span>{leido === null ? '' : `${cuenta} · ${String(archivo.kb)} KB`}</span>
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                entrada.current?.click();
                            }}
                        >
                            Cambiar
                        </button>
                    </div>
                )}
                {errores.archivo !== null && (
                    <span role="alert" className="rv-boy-error">
                        <Icono nombre="circle-alert" />
                        {errores.archivo}
                    </span>
                )}
                <Campo
                    etiqueta="Número del extracto"
                    valor={numero}
                    onChange={(v) => {
                        setNumero(v.toUpperCase());
                        setDeApi({});
                    }}
                    max={40}
                    error={errores.numero}
                    ayuda="El que trae el extracto de YCloud."
                />
                <Desplegable etiqueta="Mes del extracto" valor={periodo} opciones={meses.map((m) => ({ valor: claveMes(m), texto: textoDelMes(m) }))} onChange={setPeriodo} />
            </div>
            {fallos.length > 0 && (
                <div className="rv-boy-errores" role="alert" aria-label="Errores del archivo">
                    <strong>
                        <Icono nombre="circle-alert" />
                        {fallos.length === 1 ? 'El archivo tiene 1 error' : `El archivo tiene ${String(fallos.length)} errores`}
                    </strong>
                    {fallos.map((f) => (
                        <span key={f}>{f}</span>
                    ))}
                    <span className="rv-boy-errores__pie">Corrígelos en el CSV y vuelve a elegirlo. No se importa nada hasta que esté bien.</span>
                </div>
            )}
            {leido !== null && leido.lineas.length > 0 && (
                <div className="rv-boy-vista" role="table" aria-label="Vista previa de las líneas">
                    <div className="rv-boy-vista__cab">
                        <strong>Vista previa</strong>
                        <span>{cuenta}</span>
                    </div>
                    <div className="rv-boy-vista__caja">
                        {vista.map((l, i) => (
                            <div key={`${l.waba_ref}-${l.pricing_category}-${l.country}-${String(i)}`} className="rv-boy-vista__fila" role="row">
                                <span role="cell">
                                    <strong>{`${CATEGORIAS[l.pricing_category]} · ${pais(l.country)}`}</strong>
                                    <span>{`Cuenta ${l.waba_ref} · ${String(l.billable_messages)} mensajes`}</span>
                                </span>
                                <span role="cell" className="rv-boy-num">
                                    {dinero(l.amount)}
                                </span>
                            </div>
                        ))}
                        {leido.lineas.length > vista.length && <div className="rv-boy-vista__mas">{`y ${String(leido.lineas.length - vista.length)} más`}</div>}
                    </div>
                </div>
            )}
            <p className="rv-boy-nota">
                <Icono nombre="info" />
                <span>Se concilia al importar: al terminar verás las diferencias con lo que hemos contado. Importar dos veces el mismo extracto no duplica líneas.</span>
            </p>
        </PanelLateral>
    );
}

/**
 * **Recargas del monedero** de YCloud (C.69, G1157, G1154): en un panel, la nueva recarga (importe en dólares y referencia,
 * `createYCloudWalletTopUp`) y la lista (`listYCloudWalletTopUps`), con «Nueva» en la que se acaba de registrar.
 */
export function PanelRecargas(p: { abierto: boolean; ops: Operaciones; movil: boolean; recargas: readonly RecargaDelMonedero[] | null; ahora: Date; onCerrar: () => void; onHecho: () => void }) {
    return p.abierto ? <FormRecargas {...p} /> : null;
}

function FormRecargas({ ops, movil, recargas, ahora, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; recargas: readonly RecargaDelMonedero[] | null; ahora: Date; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [importe, setImporte] = useState('');
    const [referencia, setReferencia] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [nueva, setNueva] = useState<string | null>(null);
    const [deApi, setDeApi] = useState<Partial<Record<'importe' | 'referencia', string>>>({});
    const centimos = aCentimos(importe);
    const errores = {
        importe: deApi.importe ?? (intentado && (centimos === null || centimos === 0) ? ERROR_IMPORTE_RECARGA : null),
        referencia: deApi.referencia ?? (intentado && referencia.trim() === '' ? ERROR_REFERENCIA_RECARGA : null),
    };
    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (centimos === null || centimos === 0 || referencia.trim() === '') {
            return;
        }
        setOcupado(true);
        ops.createYCloudWalletTopUp({ amount: { amount: centimos, currency: MONEDA_MONEDERO }, reference: referencia.trim() })
            .then((r) => {
                setOcupado(false);
                setNueva(r.id);
                setImporte('');
                setReferencia('');
                setIntentado(false);
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const api = { importe: errorDeCampo(e, 'amount') ?? errorDeCampo(e, 'amount.amount'), referencia: errorDeCampo(e, 'reference') };
                if (Object.values(api).some((x) => x !== null)) {
                    setDeApi(Object.fromEntries(Object.entries(api).filter(([, x]) => x !== null)));
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Recargas del monedero"
            eyebrow="Conciliación YCloud"
            subtitulo="El prepago de Reservaria en YCloud, en dólares."
            icono="wallet"
            accion="Registrar la recarga"
            accionIcono="check"
            ocupado={ocupado}
            ocupadoTexto="Registrando…"
            onAccion={guardar}
        >
            <p className="rv-boy-aviso">
                <Icono nombre="info" />
                <span>{AVISO_MONEDERO}</span>
            </p>
            <div className="rv-boy-form">
                <strong className="rv-boy-subtitulo">Nueva recarga</strong>
                <div className="rv-boy-recarga-campos">
                    <Campo
                        etiqueta="Importe en dólares"
                        tipo="number"
                        valor={importe}
                        prefijo="$ "
                        onChange={(v) => {
                            setImporte(v);
                            setDeApi({});
                        }}
                        error={errores.importe}
                    />
                    <Campo
                        etiqueta="Referencia del pago"
                        valor={referencia}
                        onChange={(v) => {
                            setReferencia(v.slice(0, 120));
                            setDeApi({});
                        }}
                        max={120}
                        error={errores.referencia}
                        ayuda="La del justificante del pago a YCloud."
                        onEnter={guardar}
                    />
                </div>
            </div>
            <div className="rv-boy-vista" role="region" aria-label="Recargas">
                <div className="rv-boy-vista__cab">
                    <strong className="rv-boy-subtitulo">Recargas</strong>
                    <span>Quedan en Auditoría</span>
                </div>
                {recargas !== null && (
                    <div className="rv-boy-vista__caja">
                        {recargas.length === 0 && <div className="rv-boy-vista__mas">Aún no hay recargas registradas.</div>}
                        {recargas.map((r) => {
                            const f = filaRecarga(r, ahora);
                            return (
                                <div key={r.id} className="rv-boy-recarga" role="group" aria-label={`Recarga del ${f.fecha}`}>
                                    <span className="rv-boy-recarga__baldosa"><Icono nombre="wallet" /></span>
                                    <span className="rv-boy-recarga__textos">
                                        <strong>
                                            {f.fecha}
                                            {r.id === nueva && <Chip estado="confirmed">Nueva</Chip>}
                                        </strong>
                                        <span>{f.referencia}</span>
                                    </span>
                                    <span className="rv-boy-recarga__cifra">{f.importe}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}
